import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Headphones, LockKeyhole, MessageCircleMore, Play } from 'lucide-react'
import { AudioPlayer } from './AudioPlayer'
import { CURRENT_TRAIL_ORDER, TRAIL_NAME, TRAIL_WORKS } from '../data/trail'
import type { Activity } from '../types'

interface Props {
  activities: Activity[]
  workTitle: string
  workComposer: string
}

const MOBILE_QUERY = '(max-width: 720px)'

const HORIZONTAL = { step: 176, padding: 72, amplitude: 74, baseline: 158, cross: 320 }
const VERTICAL = { step: 168, padding: 64, amplitude: 100, baseline: 190, cross: 380 }
const ART_PATH_PUSH = 86

function wavePath(points: { x: number; y: number }[]) {
  if (points.length < 2) return ''
  let d = `M ${points[0].x} ${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i]
    const p1 = points[i + 1]
    const mx = (p0.x + p1.x) / 2
    const my = (p0.y + p1.y) / 2
    d += ` Q ${p0.x} ${p0.y} ${mx} ${my}`
  }
  const last = points[points.length - 1]
  d += ` L ${last.x} ${last.y}`
  return d
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches)
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

function TopicIcon({ activity }: { activity: Activity }) {
  if (activity.status === 'completed') return <Check />
  if (activity.status === 'locked') return <LockKeyhole />
  if (activity.kind === 'botelho') return <MessageCircleMore />
  if (activity.kind === 'guided') return <Headphones />
  return <Play fill="currentColor" />
}

export function TrailMap({ activities, workTitle, workComposer }: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [selected, setSelected] = useState(CURRENT_TRAIL_ORDER)
  const isMobile = useIsMobile()

  const layout = isMobile ? VERTICAL : HORIZONTAL
  const points = useMemo(() => TRAIL_WORKS.map((work, i) => (
    isMobile
      ? { x: layout.baseline + layout.amplitude * Math.sin(i * 0.85), y: layout.padding + i * layout.step }
      : { x: layout.padding + i * layout.step, y: layout.baseline + layout.amplitude * Math.sin(i * 0.85) }
  )), [isMobile, layout])
  // A linha guia contorna as obras ilustradas: em vez de cruzar o centro da arte, o ponto do
  // traçado é empurrado para a base da imagem, dando a impressão de que o caminho passa ao redor dela.
  const pathPoints = useMemo(() => points.map((p, i) => {
    if (!TRAIL_WORKS[i].image) return p
    return isMobile
      ? { x: layout.baseline + (layout.amplitude + ART_PATH_PUSH) * Math.sin(i * 0.85), y: p.y }
      : { x: p.x, y: layout.baseline + layout.amplitude * Math.sin(i * 0.85) + ART_PATH_PUSH }
  }), [points, isMobile, layout])
  const pathD = useMemo(() => wavePath(pathPoints), [pathPoints])
  const mainAxisSize = layout.padding * 2 + (TRAIL_WORKS.length - 1) * layout.step
  const canvasWidth = isMobile ? layout.cross : mainAxisSize
  const canvasHeight = isMobile ? mainAxisSize : layout.cross

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const current = points[CURRENT_TRAIL_ORDER - 1]
    if (!current) return
    if (isMobile) el.scrollTo({ top: Math.max(0, current.y - el.clientHeight / 2), behavior: 'auto' })
    else el.scrollTo({ left: Math.max(0, current.x - el.clientWidth / 2), behavior: 'auto' })
  }, [points, isMobile])

  const selectedWork = TRAIL_WORKS.find((work) => work.order === selected)

  return (
    <section className="trail-map" aria-labelledby="trail-map-title">
      <header className="trail-map__header">
        <div><p className="step-label">Trilha</p><h2 id="trail-map-title">{TRAIL_NAME}</h2></div>
      </header>
      <div className={`trail-map__scroller ${isMobile ? 'trail-map__scroller--vertical' : ''}`} ref={scrollerRef}>
        <div className="trail-map__canvas" style={{ width: canvasWidth, height: canvasHeight }}>
          <svg className="trail-map__wave" width={canvasWidth} height={canvasHeight} viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}>
            <path className="trail-map__wave-shadow" d={pathD} />
            <path className="trail-map__wave-line" d={pathD} />
          </svg>
          {TRAIL_WORKS.map((work, i) => {
            const state = work.order === CURRENT_TRAIL_ORDER ? 'current' : work.audioUrl ? 'preview' : 'locked'
            const { x, y } = points[i]
            return (
              <button
                type="button"
                key={work.order}
                className={`trail-node trail-node--${state} ${work.image ? 'trail-node--has-art' : ''} ${selected === work.order ? 'is-selected' : ''}`}
                style={{ left: x, top: y }}
                onClick={() => setSelected(work.order)}
              >
                {work.order === CURRENT_TRAIL_ORDER && <span className="trail-node__you">VOCÊ ESTÁ AQUI</span>}
                {work.image ? (
                  <span className="trail-node__art-wrap">
                    <img src={work.image} alt="" className="trail-node__art" loading="lazy" decoding="async" />
                    {state !== 'current' && <span className="trail-node__badge">{state === 'preview' ? <Headphones /> : <LockKeyhole />}</span>}
                  </span>
                ) : (
                  <span className="trail-node__dot">{state === 'current' ? <Play fill="currentColor" /> : state === 'preview' ? <Headphones /> : <LockKeyhole />}</span>
                )}
                <span className="trail-node__label">{work.title}</span>
              </button>
            )
          })}
        </div>
      </div>
      <p className="trail-map__caption"><span>♪</span> A pausa também é parte da música — entre uma obra e outra.</p>
      {selectedWork && (
        <div className="trail-map__detail">
          {selectedWork.order === CURRENT_TRAIL_ORDER ? (
            <>
              <p className="step-label">{workComposer}</p>
              <h3>{workTitle}</h3>
              <ul className="trail-map__topics">
                {activities.map((activity) => (
                  <li key={activity.id}>
                    {activity.status === 'locked'
                      ? <span className="trail-map__topic trail-map__topic--locked"><TopicIcon activity={activity} />{activity.eyebrow}</span>
                      : <Link className="trail-map__topic" to={`/atividade/${activity.id}`}><TopicIcon activity={activity} />{activity.eyebrow}</Link>}
                  </li>
                ))}
              </ul>
            </>
          ) : selectedWork.audioUrl ? (
            <div className="trail-map__preview">
              <p className="step-label">{selectedWork.composer}</p>
              <h3>{selectedWork.title}</h3>
              <div className="listening-room listening-room--compact">
                <AudioPlayer key={selectedWork.order} src={selectedWork.audioUrl} />
              </div>
              <p className="license-note">{selectedWork.attribution} · {selectedWork.license}</p>
              <p className="trail-map__preview-note">Prévia — os três encontros desta obra ainda não foram escritos.</p>
            </div>
          ) : (
            <div className="trail-map__soon">
              <LockKeyhole />
              <div><p className="step-label">{selectedWork.composer}</p><h3>{selectedWork.title}</h3><p>Em breve nesta trilha.</p></div>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
